import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip,
  OutlinedInput,
  CircularProgress,
  IconButton,
  Grid,
  Autocomplete,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { authenticatedFetch } from "../../utils/api";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface Company {
  id: number;
  name: string;
}

interface ReceivedNote {
  id: number;
  company: {
    id: number;
    name: string;
  };
  total_bags: number;
  total_weight: number;
  counts_display: string;
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

const createProgramDetailSchema = (machines: Machine[]) => z.object({
  dia: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Dia is required" }))
    .refine((val) => Number.isInteger(val), "Dia must be an integer")
    .refine((val) => val > 0, "Dia must be positive"),
  gauge: z.enum(["14", "16"], {
    required_error: "Gauge is required",
  }),
  program_type: z.enum(["rib", "fine"], {
    required_error: "Type is required",
  }),
  weight_in_kilo: z
    .union([z.number(), z.string()])
    .pipe(z.coerce.number({ required_error: "Weight is required" }))
    .refine((val) => val > 0, "Weight must be positive"),
  number_of_rolls: z
    .union([z.number(), z.string(), z.undefined(), z.null()])
    .transform((val) => val === "" || val === null || val === undefined ? undefined : Number(val))
    .optional()
    .refine((val) => val === undefined || val > 0, "Number of rolls must be positive"),
  design: z.string().optional(),
  texture: z
    .union([z.number(), z.string(), z.undefined(), z.null()])
    .transform((val) => val === "" || val === null || val === undefined ? undefined : Number(val))
    .optional()
    .refine((val) => val === undefined || (Number.isInteger(val) && val > 0), "Texture must be a positive integer"),
}).refine(
  (data) => {
    // Check if the combination of dia, gauge, and program_type exists in machines
    return machines.some(
      (machine) =>
        machine.dia === data.dia &&
        machine.gauge === data.gauge &&
        machine.machine_type === data.program_type
    );
  },
  {
    message: "This combination of Dia, Gauge, and Type does not exist in available machines",
    path: ["dia"], // Show error on dia field
  }
);

const createProgramSchema = (machines: Machine[]) => z.object({
  company_id: z.number({ required_error: "Company is required" }),
  lot: z.string().optional(),
  status: z.enum(["not_started", "in_progress", "completed", "delivered"], {
    required_error: "Status is required",
  }),
  received_note_ids: z.array(z.number()).optional(),
  program_details_attributes: z
    .array(createProgramDetailSchema(machines))
    .min(1, "At least one program detail is required"),
});

type ProgramFormData = z.infer<ReturnType<typeof createProgramSchema>>;

export default function CreateProgram() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string>("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [receivedNotes, setReceivedNotes] = useState<ReceivedNote[]>([]);
  const [filteredReceivedNotes, setFilteredReceivedNotes] = useState<ReceivedNote[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);

  const form = useForm<ProgramFormData>({
    defaultValues: {
      company_id: undefined,
      lot: "",
      status: "not_started" as const,
      received_note_ids: [],
      program_details_attributes: [
        {
          dia: undefined as any,
          gauge: undefined as any,
          program_type: undefined as any,
          weight_in_kilo: undefined as any,
          number_of_rolls: undefined as any,
          design: "",
          texture: undefined as any,
        },
      ],
    },
  });

  const { control, handleSubmit, watch, reset, setError: setFieldError, clearErrors, setValue } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "program_details_attributes",
  });

  const selectedCompanyId = watch("company_id");
  const programDetails = watch("program_details_attributes");

  useEffect(() => {
    fetchData();
  }, []);

  // Update the form resolver when machines are loaded
  useEffect(() => {
    if (machines.length > 0) {
      // Manually validate the program details to check machine combinations
      const details = watch("program_details_attributes");
      details.forEach((detail, index) => {
        if (detail.dia && detail.gauge && detail.program_type) {
          const machineExists = machines.some(
            (machine) =>
              machine.dia === detail.dia &&
              machine.gauge === detail.gauge &&
              machine.machine_type === detail.program_type
          );
          if (!machineExists) {
            setFieldError(`program_details_attributes.${index}.dia`, {
              type: "manual",
              message: `Dia ${detail.dia} with ${detail.gauge} GG and ${detail.program_type} type is not available in machines`,
            });
          } else {
            clearErrors(`program_details_attributes.${index}.dia`);
          }
        }
      });
    }
  }, [machines, programDetails, setFieldError, clearErrors, watch]);

  useEffect(() => {
    if (selectedCompanyId) {
      const filtered = receivedNotes.filter(
        (receivedNote) => receivedNote.company.id === selectedCompanyId
      );
      setFilteredReceivedNotes(filtered);
    } else {
      setFilteredReceivedNotes([]);
    }
    // Clear selected receipts when company changes
    setValue("received_note_ids", []);
  }, [selectedCompanyId, receivedNotes, setValue]);

  const fetchData = async () => {
    try {
      const [companiesRes, receivedNotesRes, machinesRes] = await Promise.all([
        authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`),
        authenticatedFetch(`${API_URL}/received_notes?per_page=1000`),
        authenticatedFetch(`${API_URL}/machines`),
      ]);

      if (companiesRes.status === 401 || receivedNotesRes.status === 401 || machinesRes.status === 401) {
        navigate("/login");
        return;
      }

      if (!companiesRes.ok || !receivedNotesRes.ok || !machinesRes.ok) {
        throw new Error("Failed to fetch data");
      }

      const companiesData = await companiesRes.json();
      const receivedNotesData = await receivedNotesRes.json();
      const machinesData = await machinesRes.json();

      setCompanies(companiesData);
      setReceivedNotes(receivedNotesData);
      setMachines(machinesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: ProgramFormData) => {
    try {
      // Validate that all program detail combinations exist in machines
      let hasValidationError = false;
      data.program_details_attributes.forEach((detail, index) => {
        const machineExists = machines.some(
          (machine) =>
            machine.dia === detail.dia &&
            machine.gauge === detail.gauge &&
            machine.machine_type === detail.program_type
        );
        if (!machineExists) {
          setFieldError(`program_details_attributes.${index}.dia`, {
            type: "manual",
            message: `Dia ${detail.dia} with ${detail.gauge} GG and ${detail.program_type} type is not available in machines`,
          });
          hasValidationError = true;
        }
      });

      if (hasValidationError) {
        setError("Please fix the validation errors before submitting");
        return;
      }

      const response = await authenticatedFetch(`${API_URL}/programs`, {
        method: "POST",
        body: JSON.stringify({ program: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      navigate("/programs");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const calculateTotalWeight = () => {
    return programDetails
      ?.reduce((sum, detail) => sum + (detail?.weight_in_kilo || 0), 0)
      .toFixed(2);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" component="h2">
              {t('programs.createTitle')}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/programs")}
              variant="outlined"
            >
              {t('back')}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="company_id"
              control={control}
              render={({ field: { onChange, value }, fieldState }) => {
                const selectedCompany = companies.find(c => c.id === value);
                
                return (
                  <Autocomplete
                    options={companies}
                    getOptionLabel={(option) => option.name}
                    value={selectedCompany || null}
                    onChange={(_, newValue) => {
                      onChange(newValue ? newValue.id : null);
                    }}
                    filterOptions={(options, params) => {
                      const filtered = options.filter((option) =>
                        option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                      );
                      return filtered;
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('programs.company')}
                        margin="normal"
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message || 
                          t('programs.typeToSearchCompany')
                        }
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                );
              }}
            />

            <Controller
              name="lot"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('programs.lotOptional')}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                >
                  <InputLabel>{t('status')}</InputLabel>
                  <Select {...field} label={t('status')} value={field.value || "not_started"}>
                    <MenuItem value="not_started">
                      <Chip
                        label={t('programs.statusNotStarted')}
                        size="small"
                        sx={{ bgcolor: "#9e9e9e", color: "#fff" }}
                      />
                    </MenuItem>
                    <MenuItem value="in_progress">
                      <Chip
                        label={t('programs.statusInProgress')}
                        size="small"
                        sx={{ bgcolor: "#fdd835", color: "#000" }}
                      />
                    </MenuItem>
                    <MenuItem value="completed">
                      <Chip
                        label={t('programs.statusCompleted')}
                        size="small"
                        sx={{ bgcolor: "#66bb6a", color: "#fff" }}
                      />
                    </MenuItem>
                    <MenuItem value="delivered">
                      <Chip
                        label={t('programs.statusDelivered')}
                        size="small"
                        sx={{ bgcolor: "#2196f3", color: "#fff" }}
                      />
                    </MenuItem>
                  </Select>
                  {fieldState.error && (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Box sx={{ mt: 3, mb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">{t('programs.programDetails')}</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    append({
                      dia: undefined as any,
                      gauge: undefined as any,
                      program_type: undefined as any,
                      weight_in_kilo: undefined as any,
                      number_of_rolls: undefined as any,
                      design: "",
                      texture: undefined as any,
                    })
                  }
                >
                  {t('addDetail')}
                </Button>
              </Stack>

              <Stack spacing={2}>
                {fields.map((field, index) => (
                  <Card key={field.id} variant="outlined">
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {t('receivedNotes.detailNumber')}{index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => remove(index)}
                          disabled={fields.length === 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        {/* Row 1: Dia, Gauge, Type */}
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`program_details_attributes.${index}.dia`}
                            control={control}
                            render={({ field, fieldState }) => {
                              const currentGauge = programDetails[index]?.gauge;
                              const currentType = programDetails[index]?.program_type;
                              
                              let availableDias = machines.map(m => m.dia);
                              if (currentGauge && currentType) {
                                availableDias = machines
                                  .filter(m => m.gauge === currentGauge && m.machine_type === currentType)
                                  .map(m => m.dia);
                              } else if (currentGauge) {
                                availableDias = machines
                                  .filter(m => m.gauge === currentGauge)
                                  .map(m => m.dia);
                              } else if (currentType) {
                                availableDias = machines
                                  .filter(m => m.machine_type === currentType)
                                  .map(m => m.dia);
                              }
                              
                              const uniqueDias = Array.from(new Set(availableDias)).sort((a, b) => a - b);
                              
                              return (
                                <FormControl fullWidth size="small" error={!!fieldState.error}>
                                  <InputLabel>{t('programs.dia')}</InputLabel>
                                  <Select 
                                    {...field} 
                                    label={t('programs.dia')}
                                    value={field.value || ""}
                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                    disabled={machines.length === 0 || uniqueDias.length === 0}
                                  >
                                    {machines.length === 0 ? (
                                      <MenuItem value="" disabled>{t('programs.noMachinesAvailable')}</MenuItem>
                                    ) : uniqueDias.length === 0 ? (
                                      <MenuItem value="" disabled>{t('programs.noMachinesForSelection')}</MenuItem>
                                    ) : (
                                      uniqueDias.map((dia) => (
                                        <MenuItem key={dia} value={dia}>{dia}</MenuItem>
                                      ))
                                    )}
                                  </Select>
                                  {fieldState.error && (
                                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                                  )}
                                </FormControl>
                              );
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`program_details_attributes.${index}.gauge`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <FormControl fullWidth size="small" error={!!fieldState.error}>
                                <InputLabel>{t('programs.gaugeGG')}</InputLabel>
                                <Select {...field} label={t('programs.gaugeGG')} value={field.value || ""}>
                                  <MenuItem value="14">14 GG</MenuItem>
                                  <MenuItem value="16">16 GG</MenuItem>
                                  <MenuItem value="24">24 GG</MenuItem>
                                </Select>
                                {fieldState.error && (
                                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`program_details_attributes.${index}.program_type`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <FormControl fullWidth size="small" error={!!fieldState.error}>
                                <InputLabel>{t('programs.type')}</InputLabel>
                                <Select {...field} label={t('programs.type')} value={field.value || ""}>
                                  <MenuItem value="rib">{t('machines.rib')}</MenuItem>
                                  <MenuItem value="fine">Fine</MenuItem>
                                </Select>
                                {fieldState.error && (
                                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </Grid>

                        {/* Row 2: Weight, Rolls, Texture */}
                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`program_details_attributes.${index}.weight_in_kilo`}
                            control={control}
                            render={({
                              field: { onChange, value, ...field },
                              fieldState,
                            }) => (
                              <TextField
                                {...field}
                                label={t('programs.weightKg')}
                                value={value || ""}
                                onChange={(e) =>
                                  onChange(
                                    e.target.value
                                      ? Number(e.target.value)
                                      : undefined
                                  )
                                }
                                type="number"
                                size="small"
                                inputProps={{ step: "0.01" }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`program_details_attributes.${index}.number_of_rolls`}
                            control={control}
                            render={({
                              field: { onChange, value, ...field },
                              fieldState,
                            }) => (
                              <TextField
                                {...field}
                                label={t('programs.rollsOptional')}
                                value={value || ""}
                                onChange={(e) =>
                                  onChange(
                                    e.target.value
                                      ? Number(e.target.value)
                                      : undefined
                                  )
                                }
                                type="number"
                                size="small"
                                inputProps={{ step: "0.01" }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Controller
                            name={`program_details_attributes.${index}.texture`}
                            control={control}
                            render={({
                              field: { onChange, value, ...field },
                              fieldState,
                            }) => (
                              <TextField
                                {...field}
                                label={t('programs.textureOptional')}
                                value={value || ""}
                                onChange={(e) =>
                                  onChange(
                                    e.target.value
                                      ? Number(e.target.value)
                                      : undefined
                                  )
                                }
                                type="number"
                                size="small"
                                inputProps={{ step: "1" }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>

                        {/* Row 3: Design (Full Width) */}
                        <Grid item xs={12}>
                          <Controller
                            name={`program_details_attributes.${index}.design`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                label={t('programs.design')}
                                size="small"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                fullWidth
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}

                {/* Total Weight Card */}
                <Card variant="outlined" sx={{ bgcolor: "action.hover" }}>
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body1" fontWeight="bold">{t('programs.totalWeight')}</Typography>
                      <Typography variant="body1" fontWeight="bold">{calculateTotalWeight()} kg</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Stack>
            </Box>

            <Controller
              name="received_note_ids"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  margin="normal"
                  error={!!fieldState.error}
                  disabled={!selectedCompanyId}
                >
                  <InputLabel>{t('programs.associatedReceipts')}</InputLabel>
                  <Select
                    {...field}
                    multiple
                    value={field.value || []}
                    disabled={!selectedCompanyId}
                    input={<OutlinedInput label={t('programs.associatedReceipts')} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(selected as number[]).map((value) => {
                          const receipt = filteredReceivedNotes.find(
                            (r) => r.id === value
                          );
                          return (
                            <Chip
                              key={value}
                              label={`${t('programs.receipt')} #${value} (${receipt?.counts_display || 'N/A'})`}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {filteredReceivedNotes.map((receipt) => (
                      <MenuItem key={receipt.id} value={receipt.id}>
                        {t('programs.receipt')} #{receipt.id} - {receipt.counts_display} (
                        {receipt.total_bags} {t('receivedNotes.bags')}, {typeof receipt.total_weight === 'number' ? receipt.total_weight.toFixed(2) : receipt.total_weight} kg)
                      </MenuItem>
                    ))}
                  </Select>
                  {!selectedCompanyId && (
                    <FormHelperText>
                      {t('programs.pleaseSelectCompanyFirst')}
                    </FormHelperText>
                  )}
                  {filteredReceivedNotes.length === 0 && selectedCompanyId && (
                    <FormHelperText>
                      {t('programs.noReceiptsForCompany')}
                    </FormHelperText>
                  )}
                  {fieldState.error && (
                    <FormHelperText>{fieldState.error.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button type="submit" variant="contained" fullWidth>
                {t('programs.createTitle')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/programs")}
                fullWidth
              >
                {t('cancel')}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
